import React, { useEffect, useRef, useState } from 'react';
import { X, Coins } from 'lucide-react';
import type { View } from '../../lib/viewRouter';
import { Logo } from './Logo';
import { UserProgress } from './UserProgress';
import { useWalletStore } from '../../stores/walletStore';
import { CoinAmount } from '../CoinAmount';
import { PageGuideButton } from '../guide/PageGuideButton';


export interface NavDestination {
  id: View;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  items: NavDestination[];
  currentView: View;
  onSelect: (view: View) => void;
  isActive: (id: View) => boolean;
}

/**
 * Mobile left-hand navigation drawer. Opened by the hamburger in the Header
 * (top-left); slides in from the left over the screen as a full side-nav menu.
 * A11y: role=dialog + aria-modal, Esc closes, backdrop closes, focus moves into
 * the drawer on open and returns to the opener on close. Rows are >=44px.
 */
export function NavDrawer({ open, onClose, items, currentView, onSelect, isActive }: NavDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Keep the drawer mounted through its exit transition so it can animate out.
  const [mounted, setMounted] = useState(open);
  // Drives the enter/exit transition (slide + fade); flipped a frame after mount.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Next tick (after the off-screen state paints) → slide/fade in. A timeout
      // (not rAF) so it still fires if the tab/page is currently hidden.
      const id = setTimeout(() => setVisible(true), 20);
      return () => clearTimeout(id);
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), 300); // match transition duration
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const focusId = requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>('button')?.focus();
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(focusId);
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  if (!mounted) return null;

  return (
    <div className="lg:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Navigation menu">
      {/* Backdrop — fades in/out */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className={`absolute inset-0 bg-foreground/40 backdrop-blur-sm transition-opacity duration-300 ease-out motion-reduce:transition-none ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Panel — slides in from the left */}
      <div
        ref={panelRef}
        className={`absolute inset-y-0 left-0 w-72 max-w-[85%] bg-card border-r border-border shadow-xl safe-area-pt flex flex-col transition-transform duration-300 ease-out motion-reduce:transition-none ${
          visible ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <Logo className="h-6 w-auto" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User progress (avatar + level ring) — mirrors the desktop rail */}
        <UserProgress onNavigate={onClose} />

        <nav aria-label="All destinations" className="flex-1 overflow-y-auto p-2 space-y-1">
          {items.map(({ id, icon: Icon, label }) => {
            const active = isActive(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => {
                  onSelect(id);
                  onClose();
                }}
                aria-current={active ? 'page' : undefined}
                className={`flex min-h-[3rem] w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  active ? 'bg-primary/10 text-primary nav-active-contrast' : 'text-foreground hover:bg-secondary'
                }`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </nav>

        {/* ⭐ "Guide for this page" — the drawer's "?" (it closes the drawer first). */}
        <PageGuideButton variant="drawer" onBeforeStart={onClose} />

        {/* Wallet — pinned at the bottom, mirrors the desktop rail widget */}
        <WalletDrawerWidget
          /* ⭐ Its own view since 2026-08-12 — see the desktop rail widget. */
          onOpen={() => {
            onSelect('wallet');
            onClose();
          }}
        />
      </div>
    </div>
  );
}

function WalletDrawerWidget({ onOpen }: { onOpen: () => void }) {
  const balanceCp = useWalletStore((s) => s.balanceCp);
  return (
    <div className="p-2 border-t border-border">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-h-[3rem] w-full items-center gap-3 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <Coins className="h-5 w-5 shrink-0 text-amber-400" />
        <span className="flex-1 flex items-center justify-between min-w-0">
          <span>Wallet</span>
          <span className="font-semibold">
            {/* ⚠️ Exact, not `compact` — the mobile mirror of the desktop rail
                widget, and a balance may never round up. See Navigation.tsx. */}
            {balanceCp === null ? '…' : <CoinAmount cp={balanceCp} />}
          </span>
        </span>
      </button>
    </div>
  );
}
